import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import './BouncedMessageStyle.css'
import '../ModalStyle.css'
import { Button, DataGridComponent, SearchWithFilter } from '@components/common'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { ICampaignMessage } from '@dataTypes/marketing.type'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { resendBouncedSms } from '@store/actions'

type BouncedMessageProps = {
  data: ICampaignMessage[]
  filterSelectionList: {
    campaignName: string[]
    errorType: string[]
  }
  show: boolean
  onHide: () => void
}

const BouncedMessageModal = ({
  data,
  filterSelectionList,
  show,
  onHide,
}: BouncedMessageProps): React.ReactElement => {
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const dispatch = useAppDispatch()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [bouncedMessages, setBouncedMessages] =
    useState<ICampaignMessage[]>(data)
  const [loadingMessage, setLoadingMessage] = useState<string>('')
  const [messageFilters, setMessageFilters] = useState<FilterSelection>({
    campaignName: [],
    errorType: [],
  })

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value)
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    setMessageFilters({ ...messageFilters, ...selectedFilters })
  }

  const handleResend = (message: ICampaignMessage): void => {
    if (message.campaign_message_id) {
      setLoadingMessage(message.campaign_message_id)
    }
    dispatch(resendBouncedSms(message)).then((res) => {
      if (res.payload) {
        setBouncedMessages(
          bouncedMessages.map((m) =>
            m.campaign_message_id === message.campaign_message_id
              ? { ...m, ...(res.payload as Partial<ICampaignMessage>) }
              : m,
          ),
        )
        setLoadingMessage('')
      }
    })
  }

  const columns: ColumnModel[] = useMemo(
    () => [
      {
        headerText: 'Contact name',
        field: 'contact.first_name',
        template: (props: ICampaignMessage) => (
          <div tabIndex={0} className="name-col">
            <span className="name">{getFullName(props.contact, true)}</span>
            <span className="cellphone">
              {props.contact?.cellphone
                ? formatPhoneNumber(props.contact?.cellphone, true)
                : ''}
            </span>
          </div>
        ),
        width: 0.29,
      },
      {
        headerText: 'Campaign name',
        field: 'campaign_name',
        width: 0.29,
      },
      {
        headerText: 'Error type',
        field: 'error_reason',
        template: (props: ICampaignMessage) => (
          <span className="error-badge badge">{props.error_reason}</span>
        ),
        width: 0.27,
      },
      {
        headerText: 'Action',
        template: (props: ICampaignMessage) => (
          <Button
            className="resend-button button-primary"
            size="sm"
            disabled={
              ['Sent', 'Pending'].includes(props.status) ||
              props.error_code === '21211'
            }
            onClick={() => handleResend(props)}
          >
            {loadingMessage === props.campaign_message_id ? (
              <Spinner
                animation="border"
                variant="primary"
                role="status"
                className="resend-spinner"
              />
            ) : props.status === 'Failed' ? (
              'Resend'
            ) : (
              'Sent'
            )}
          </Button>
        ),
        width: 0.16,
      },
    ],
    [data],
  )

  useEffect(() => {
    let _data = [...data]
    if (searchQuery.length > 0) {
      _data = _data.filter((item) =>
        getFullName(item.contact)
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()),
      )
    }

    if (messageFilters.campaignName.length > 0) {
      _data = _data.filter(
        (item) =>
          item.campaign_name &&
          messageFilters.campaignName.includes(item.campaign_name),
      )
    }

    if (messageFilters.errorType.length > 0) {
      _data = _data.filter(
        (item) =>
          item.error_reason &&
          messageFilters.errorType.includes(item.error_reason),
      )
    }
    setBouncedMessages(_data)
  }, [data, searchQuery, messageFilters, campaigns])

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      className="bounced-message-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Bounced messages</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filter-add-container">
          <SearchWithFilter
            searchIcon="mgc_search_2_line"
            filterIcon="mgc_filter_2_line"
            placeholder="Search"
            searchQuery={searchQuery}
            onChange={handleSearch}
            className="mcgm-filter"
            filters={filterSelectionList}
            onFilterChange={handleFilterChange}
            filterLabels={{
              campaignName: 'Campaign name',
              errorType: 'Error type',
            }}
          />
        </div>
        <DataGridComponent
          data={bouncedMessages}
          columns={columns}
          pageSize={4}
          allowPaging={true}
          customCSS="view-list-grid"
        />
      </Modal.Body>
    </Modal>
  )
}

export default BouncedMessageModal
